import type {
  PayRunApprovalDto,
  PayRunCalculationStatus,
  PayRunCalculationSummaryDto,
  PayRunFinalisationDto,
} from './payRunsApiTypes';
import type { PayRunExceptionsPage } from './payRunPopulationApiTypes';

export const PAY_RUN_POLL_INTERVALS = {
  calculation: 1500,
  complianceStabilisation: 1500,
  approvalUnsubmitted: 5000,
  approvalSubmitted: 3000,
  detailDiscovery: 5000,
  downstream: 2000,
  focusThrottle: 5000,
} as const;

export const COMPLIANCE_STABILISATION_MAX_ATTEMPTS = 20;

const ACTIVE_CALCULATION_STATUSES = new Set<PayRunCalculationStatus>([
  'admitted',
  'calculating',
  'cancellation_requested',
]);

export const isActiveCalculationStatus = (status?: PayRunCalculationStatus | null): boolean =>
  Boolean(status && ACTIVE_CALCULATION_STATUSES.has(status));

export const shouldDiscoverPayRunDetail = (
  workflowStatus: string | undefined,
  calculationStatus?: PayRunCalculationStatus | null,
): boolean => (
  workflowStatus === 'eft_ready'
  || ((workflowStatus === 'open' || workflowStatus === 'compliance')
    && !isActiveCalculationStatus(calculationStatus))
);

export const approvalPollInterval = (
  workflowStatus: string | undefined,
  approval: PayRunApprovalDto | null,
): number | false => {
  if (workflowStatus !== 'ready_to_pay' || approval?.isSuperseded) return false;
  return approval?.submittedAt
    ? PAY_RUN_POLL_INTERVALS.approvalSubmitted
    : PAY_RUN_POLL_INTERVALS.approvalUnsubmitted;
};

export const hasActiveDownstreamLeg = (finalisation?: PayRunFinalisationDto | null): boolean =>
  finalisation?.legs.some((leg) => leg.status === 'pending' || leg.status === 'processing') ?? false;

export const exceptionEvaluationIdentity = (exceptions?: PayRunExceptionsPage | null): string =>
  `${exceptions?.evaluationId ?? ''}:${exceptions?.evaluatedAt ?? ''}`;

export const calculationReloadIdentity = (calculation?: PayRunCalculationSummaryDto | null): string | null => {
  if (!calculation?.attemptId || isActiveCalculationStatus(calculation.status)) return null;
  return `${calculation.attemptId}:${calculation.completedAt ?? calculation.status}`;
};

export const isComplianceEvaluationStabilised = (
  previousIdentity: string,
  exceptions: PayRunExceptionsPage,
): boolean => complianceEvaluationStabilisationResult(previousIdentity, exceptions) === 'completed';

const TERMINAL_NON_SUCCESS_COMPLIANCE_STATUSES = new Set([
  'incomplete',
  'failed',
  'stale',
  'conflict',
  'locked',
  'disabled',
]);

export type ComplianceEvaluationStabilisationResult = 'continue' | 'completed' | 'terminal_non_success';

export const complianceEvaluationStabilisationResult = (
  previousIdentity: string,
  exceptions: PayRunExceptionsPage,
): ComplianceEvaluationStabilisationResult => {
  if (exceptionEvaluationIdentity(exceptions) === previousIdentity) return 'continue';
  if (!exceptions.isStale && exceptions.evaluationStatus === 'completed') return 'completed';
  return exceptions.evaluationStatus && TERMINAL_NON_SUCCESS_COMPLIANCE_STATUSES.has(exceptions.evaluationStatus)
    ? 'terminal_non_success'
    : 'continue';
};

export const approvalFingerprint = (approval: PayRunApprovalDto): string => JSON.stringify({
  contextId: approval.contextId,
  submittedAt: approval.submittedAt ?? null,
  isSuperseded: approval.isSuperseded,
  canSubmit: approval.canSubmit,
  streams: approval.streams.map((stream) => ({
    approvalStream: stream.approvalStream,
    requiredApprovalCount: stream.requiredApprovalCount,
    requirements: stream.requirements.map((requirement) => ({
      requirementId: requirement.requirementId,
      status: requirement.status,
      isSuperseded: requirement.isSuperseded,
      canApprove: requirement.canApprove,
      canReject: requirement.canReject,
    })),
  })),
});
